import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import type { TranscriptSpeakerParticipantMetadata } from "@/modules/audio-recording/transcript-types";
import {
  extractRecallCompositeParticipantId,
  isRecallNonIdentityParticipantName,
  normalizeSpeakerParticipantName,
  resolveRecallNonIdentitySpeakerLabelFromExtraData,
  sanitizeRecallSpeakerLabelExtraData,
} from "@/modules/audio-recording/transcript-speaker-labels";
import { resolveTranscriptSpeakerIsPlatformHost } from "@/modules/audio-recording/transcript-speaker-platform-host";
/* eslint-disable @typescript-eslint/no-explicit-any */

export type DesktopRealtimeEventType =
  | "transcript.data"
  | "transcript.partial_data"
  | "audio_mixed_raw.data"
  | "participant_events.join"
  | "participant_events.update"
  | "participant_events.leave";

export type ParsedParticipantEventType = Extract<
  DesktopRealtimeEventType,
  "participant_events.join" | "participant_events.update" | "participant_events.leave"
>;

export type ParsedTranscriptWord = {
  text: string;
  start: number; // ms
  end: number; // ms
  confidence: number;
};

export type ParsedTranscriptEvent = {
  type: Extract<DesktopRealtimeEventType, "transcript.data" | "transcript.partial_data">;
  transcript: string;
  endOfTurn: boolean;
  timestamp: number; // ms
  words: ParsedTranscriptWord[];
  /** Internal turn-stitching key. Do not persist as participant identity. */
  speakerGroupingKey?: string;
  participantId?: number | string;
  recallSpeakerLabel?: string;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata;
  recordingId?: string;
  transcriptId?: string;
  windowId?: string;
};

export type ParsedAudioEvent = {
  type: Extract<DesktopRealtimeEventType, "audio_mixed_raw.data">;
  bufferBase64: string;
  timestamp: number; // ms
  windowId?: string;
};

export type ParsedParticipantEvent = {
  type: ParsedParticipantEventType;
  participantId?: number | string;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata;
  recordingId?: string;
  windowId?: string;
};

export type ParsedDesktopEvent =
  | ParsedTranscriptEvent
  | ParsedAudioEvent
  | ParsedParticipantEvent
  | null;

export function parseDesktopRealtimeEnvelope(input: {
  event: unknown;
  kind?: string;
}): ParsedDesktopEvent {
  try {
    const maybeEnvelope = input as { event?: unknown; kind?: string } | undefined;
    const raw = maybeEnvelope?.event;

    let inner: any = raw;
    if (typeof raw === "string") {
      inner = JSON.parse(raw);
    }

    if (inner && typeof inner === "object" && "payload" in inner && inner.payload) {
      inner = inner.payload;
    }

    const kind: string | undefined = inner?.event;
    const payloadContainer = inner?.data || {};
    const payloadData = payloadContainer?.data || payloadContainer || {};
    const windowId: string | undefined =
      typeof inner?.window?.id === "string" ? (inner.window.id as string) : undefined;

    if (kind === "audio_mixed_raw.data") {
      const base64 = payloadData?.buffer;
      const tsRelSec: number | undefined = payloadData?.timestamp?.relative;
      if (typeof base64 !== "string") return null;
      const timestamp = Math.round((tsRelSec ?? 0) * 1000);
      return { type: "audio_mixed_raw.data", bufferBase64: base64, timestamp, windowId };
    }

    if (kind === "transcript.data" || kind === "transcript.partial_data") {
      const wordsRaw = extractWords(payloadData);
      const transcript: string = wordsRaw
        .map((w: any) => w?.text || "")
        .join(" ")
        .trim();
      if (!transcript) return null;

      const parsedWords: ParsedTranscriptWord[] = wordsRaw.map((w: any) => ({
        text: typeof w?.text === "string" ? w.text : "",
        start: parseRelativeTimestampMs(w?.start_timestamp),
        end: parseRelativeTimestampMs(w?.end_timestamp),
        confidence: 0,
      }));

      const extractedParticipant = extractSpeakerParticipant(payloadData?.participant);
      const recordingId = extractStringIdentifier(payloadContainer?.recording?.id);
      const transcriptId = extractStringIdentifier(payloadContainer?.transcript?.id);
      const timestamp = resolveEventTimestamp(wordsRaw);

      return {
        type: kind,
        transcript,
        endOfTurn: kind === "transcript.data",
        timestamp,
        words: parsedWords,
        ...(extractedParticipant.participantId !== undefined
          ? { participantId: extractedParticipant.participantId }
          : {}),
        ...(extractedParticipant.speakerGroupingKey !== undefined
          ? { speakerGroupingKey: extractedParticipant.speakerGroupingKey }
          : {}),
        ...(extractedParticipant.recallSpeakerLabel
          ? { recallSpeakerLabel: extractedParticipant.recallSpeakerLabel }
          : {}),
        ...(extractedParticipant.speakerParticipant
          ? { speakerParticipant: extractedParticipant.speakerParticipant }
          : {}),
        recordingId,
        transcriptId,
        windowId,
      };
    }

    if (
      kind === "participant_events.join" ||
      kind === "participant_events.update" ||
      kind === "participant_events.leave"
    ) {
      const participantSource = payloadData?.participant ?? payloadData;
      const extractedParticipant = extractSpeakerParticipant(participantSource);
      return {
        type: kind,
        ...(extractedParticipant.participantId !== undefined
          ? { participantId: extractedParticipant.participantId }
          : {}),
        ...(extractedParticipant.speakerParticipant
          ? { speakerParticipant: extractedParticipant.speakerParticipant }
          : {}),
        recordingId: extractStringIdentifier(payloadContainer?.recording?.id),
        windowId,
      };
    }

    return null;
  } catch (err) {
    logger.warn({
      message: "[desktop-event-parser] failed to parse envelope",
      info: { error: objectModule.safeErrorAsJson(err as Error) },
    });
    return null;
  }
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asPlainObject(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return undefined;
}

type ExtractedSpeakerParticipant = {
  speakerGroupingKey?: string;
  participantId?: number | string;
  recallSpeakerLabel?: string;
  speakerParticipant?: TranscriptSpeakerParticipantMetadata;
};

function extractSpeakerParticipant(participant: any): ExtractedSpeakerParticipant {
  if (!participant || typeof participant !== "object") return {};

  const rawName = normalizeSpeakerParticipantName(participant.name);
  const participantId = extractIdentifier(participant.id);
  const extraData = { ...(asPlainObject(participant.extra_data) ?? {}) };
  const email = normalizeSpeakerParticipantName(participant.email);
  const isNonIdentityParticipantName =
    rawName != null && isRecallNonIdentityParticipantName(rawName);
  const name = rawName && !isNonIdentityParticipantName ? rawName : undefined;
  const extraDataRecallSpeakerLabel =
    name === undefined && email === undefined
      ? resolveRecallNonIdentitySpeakerLabelFromExtraData(extraData)
      : undefined;
  const recallNonIdentitySpeakerLabel =
    isNonIdentityParticipantName && rawName ? rawName : extraDataRecallSpeakerLabel;
  const isNonIdentityParticipant = recallNonIdentitySpeakerLabel !== undefined;
  const compositeParticipantId = extractRecallCompositeParticipantId(recallNonIdentitySpeakerLabel);
  const id = participantId ?? compositeParticipantId;
  const isPlatformHost = resolveTranscriptSpeakerIsPlatformHost({ source: participant });
  const platform = asString(participant.platform);
  const sanitizedExtraData = sanitizeRecallSpeakerLabelExtraData(extraData);

  const speakerParticipant: TranscriptSpeakerParticipantMetadata = {};
  if (!isNonIdentityParticipant && id !== undefined) speakerParticipant.id = id;
  if (name !== undefined) speakerParticipant.name = name;
  if (!isNonIdentityParticipant && isPlatformHost !== undefined) {
    speakerParticipant.isPlatformHost = isPlatformHost;
  }
  if (!isNonIdentityParticipant && platform !== undefined) speakerParticipant.platform = platform;
  if (email !== undefined) speakerParticipant.email = email;
  if (!isNonIdentityParticipant && sanitizedExtraData !== undefined) {
    speakerParticipant.extraData = sanitizedExtraData;
  }

  return {
    ...(id !== undefined
      ? isNonIdentityParticipant
        ? { speakerGroupingKey: buildRecallNonIdentitySpeakerGroupingKey(id) }
        : { participantId: id }
      : {}),
    ...(recallNonIdentitySpeakerLabel ? { recallSpeakerLabel: recallNonIdentitySpeakerLabel } : {}),
    ...(Object.keys(speakerParticipant).length > 0 ? { speakerParticipant } : {}),
  };
}

function buildRecallNonIdentitySpeakerGroupingKey(value: number | string): string {
  return `recall-non-identity-speaker:${String(value).trim()}`;
}

function extractIdentifier(value: unknown): number | string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  return undefined;
}

function extractStringIdentifier(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  return undefined;
}

function extractWords(payloadData: unknown): any[] {
  if (!payloadData || typeof payloadData !== "object") return [];

  if (Array.isArray((payloadData as { words?: unknown }).words)) {
    return (payloadData as { words: unknown[] }).words;
  }

  const nested = (payloadData as { data?: unknown }).data;
  if (
    nested &&
    typeof nested === "object" &&
    Array.isArray((nested as { words?: unknown }).words)
  ) {
    return (nested as { words: unknown[] }).words;
  }

  return [];
}

function parseRelativeTimestampMs(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value * 1000);
  }

  if (value && typeof value === "object") {
    const maybeRelative = (value as { relative?: unknown }).relative;
    if (typeof maybeRelative === "number" && Number.isFinite(maybeRelative)) {
      return Math.round(maybeRelative * 1000);
    }

    const maybeAbsolute = (value as { absolute?: unknown }).absolute;
    if (typeof maybeAbsolute === "string") {
      const parsed = Date.parse(maybeAbsolute);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  return 0;
}

function resolveEventTimestamp(words: any[]): number {
  const last = words[words.length - 1];
  if (last) {
    const endMs = parseRelativeTimestampMs(last?.end_timestamp);
    if (endMs) return endMs;
    const startMs = parseRelativeTimestampMs(last?.start_timestamp);
    if (startMs) return startMs;
  }
  return Date.now();
}
